/**
 * Lightweight React context for the user's chosen Lichess piece set.
 *
 * Available sets are open-source Lichess piece sets bundled in /public/pieces/:
 *   alpha (default), california, cburnett, maestro, fresca
 *
 * Faction piece sets (Feb 2026): the four canonical faction-colored sets
 * ship alongside the Lichess sets — same 12-file wK/wQ/wR/wB/wN/wP + b*.svg
 * structure under /public/pieces/{faction-name}/. Each was hand-designed
 * with the faction's canonical body hex + a vivid complementary accent
 * + medallion + shield artwork, so they render standalone WITHOUT any
 * recolor pass (recolorTarget stays null for standard modes). The recolor
 * primitive still knows their body-fill hex via SET_PRIMARY_FILL in
 * PieceSvg.jsx, so seat-based pledge tints and 4-player faction tints
 * continue to work uniformly across every set.
 *
 * Choice persists in localStorage under `chess_piece_set` so it carries across
 * page reloads within the same browser session.
 */
import { createContext, useContext, useEffect, useState } from "react";

export const PIECE_SETS = [
  { value: "alpha",          label: "Alpha" },
  { value: "california",     label: "California" },
  { value: "cburnett",       label: "Cburnett" },
  { value: "maestro",        label: "Maestro" },
  { value: "fresca",         label: "Fresca" },
  // --- Faction piece sets (custom, faction-colored + accented) ---
  { value: "iron-kingdom",   label: "Iron Kingdom (Faction)" },
  { value: "dragonborn",     label: "Dragonborn (Faction)" },
  { value: "elf-court",      label: "Elf Court (Faction)" },
  { value: "celestial-host", label: "Celestial Host (Faction)" },
];

const DEFAULT_SET = "alpha";
const STORAGE_KEY = "chess_piece_set";

const Ctx = createContext({ pieceSet: DEFAULT_SET, setPieceSet: () => {} });

export function PieceSetProvider({ children }) {
  const [pieceSet, setPieceSetState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_SET;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return PIECE_SETS.some((s) => s.value === saved) ? saved : DEFAULT_SET;
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, pieceSet);
    }
  }, [pieceSet]);

  return (
    <Ctx.Provider value={{ pieceSet, setPieceSet: setPieceSetState }}>
      {children}
    </Ctx.Provider>
  );
}

export function usePieceSet() {
  return useContext(Ctx);
}
